import { useTranslation } from 'react-i18next'
import { Loader2, Download, Receipt, CreditCard, ExternalLink } from 'lucide-react'
import { usePayments, type Payment, type PaymentStatus } from '../hooks/use-payments'
import { cn } from '@/shared/lib/utils'

function formatAmount(amount: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amount / 100)
}

function formatDate(dateString: string, locale: string): string {
  return new Date(dateString).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

interface StatusBadgeProps {
  status: PaymentStatus
}

function StatusBadge({ status }: StatusBadgeProps) {
  const { t } = useTranslation()

  const statusStyles: Record<PaymentStatus, string> = {
    paid: 'bg-success/10 text-success',
    pending: 'bg-warning/10 text-warning',
    failed: 'bg-destructive/10 text-destructive',
    refunded: 'bg-foreground-muted/10 text-foreground-muted',
  }

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
        statusStyles[status]
      )}
    >
      {t(`settings.billing.status.${status}`)}
    </span>
  )
}

interface PaymentRowProps {
  payment: Payment
}

function PaymentRow({ payment }: PaymentRowProps) {
  const { t, i18n } = useTranslation()

  const getDescription = () => {
    const projectName = payment.project?.name || t('settings.billing.unknownProject')
    switch (payment.type) {
      case 'scoping':
        return t('settings.billing.typeScoping', { project: projectName })
      case 'development':
        return t('settings.billing.typeDevelopment', { project: projectName })
      case 'subscription':
        return t('settings.billing.typeSubscription')
      default:
        return payment.type
    }
  }

  const handleDownload = () => {
    if (payment.invoice_pdf_url) {
      window.open(payment.invoice_pdf_url, '_blank')
    }
  }

  return (
    <div className="flex items-center justify-between border-b border-border px-5 py-4 last:border-b-0">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">
            {getDescription()}
          </span>
          <StatusBadge status={payment.status} />
        </div>
        <div className="flex items-center gap-3 text-xs text-foreground-muted">
          <span>{formatDate(payment.created_at, i18n.language)}</span>
          {payment.invoice_number && (
            <span className="font-mono">{payment.invoice_number}</span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-4">
        <span className="text-sm font-semibold text-foreground">
          {formatAmount(payment.amount, payment.currency)}
        </span>
        {payment.status === 'paid' && (
          payment.invoice_pdf_url ? (
            <button
              onClick={handleDownload}
              className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-background-secondary"
            >
              <Download className="h-3.5 w-3.5" />
              {t('settings.billing.download')}
            </button>
          ) : (
            <span className="text-xs text-foreground-muted">
              {t('settings.billing.invoicePending')}
            </span>
          )
        )}
      </div>
    </div>
  )
}

export function BillingTab() {
  const { t } = useTranslation()
  const { data: payments, isLoading } = usePayments()

  if (isLoading) {
    return (
      <div className="flex h-48 items-center justify-center rounded-xl border border-border bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-foreground-muted" />
      </div>
    )
  }

  const hasPayments = payments && payments.length > 0

  return (
    <div className="flex flex-col gap-6">
      {/* Payment Methods Card */}
      <div className="rounded-xl border border-border bg-background">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-sm font-semibold text-foreground">
            {t('settings.billing.paymentMethods')}
          </h2>
        </div>
        <div className="p-5">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
              <CreditCard className="h-5 w-5 text-accent" />
            </div>
            <div className="flex flex-1 flex-col gap-2">
              <div>
                <span className="text-sm font-medium text-foreground">
                  {t('settings.billing.managePayments')}
                </span>
                <p className="mt-0.5 text-sm text-foreground-muted">
                  {t('settings.billing.managePaymentsDescription')}
                </p>
              </div>
              <button
                disabled
                className="flex w-fit items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground-muted transition-colors"
              >
                <ExternalLink className="h-4 w-4" />
                {t('settings.billing.openStripePortal')}
              </button>
              <p className="text-xs text-foreground-subtle">
                {t('settings.billing.stripePortalHint')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Invoices Card */}
      <div className="rounded-xl border border-border bg-background">
        <div className="border-b border-border px-5 py-4">
          <h2 className="text-sm font-semibold text-foreground">
            {t('settings.billing.invoicesTitle')}
          </h2>
        </div>

        {hasPayments ? (
          <div className="divide-y divide-border">
            {payments.map((payment) => (
              <PaymentRow key={payment.id} payment={payment} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center gap-3 py-12">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-foreground-subtle/10">
              <Receipt className="h-6 w-6 text-foreground-muted" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-foreground">
                {t('settings.billing.noInvoices')}
              </p>
              <p className="mt-1 text-sm text-foreground-muted">
                {t('settings.billing.noInvoicesDescription')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
